<template>
  <Accordion class="promo-accordion" :class="`promo-accordion--${variant}`">
    <AccordionItem
      ref="accordionItem"
      id="promo-accordion"
      :index="1"
      :variant="variant"
      :isInitiallyOpen="hasPromotions"
      :label="label"
    >
      <IconArrowDownBold v-if="!isBorderless" slot="icon" class="promo-accordion__icon" />
      <slot v-if="$slots.gift" name="gift"></slot>
      <slot name="promo"></slot>
      <template v-if="!!giftCardsTotal">
        <PromoView
          v-for="(payment, index) in giftCardPayments"
          :key="index"
          :isGiftCard="true"
          :hasPromoCode="!!giftCardsTotal"
          :discount="payment.amount | formattedCurrency(locale, currency.code, 0)"
        />
      </template>
      <p
        v-if="!!giftCardsTotal"
        class="promo-accordion__reset-payments-link"
        @click.stop="$emit('reset-payments')"
      >
        {{ $t('reset_payments') }}
      </p>
    </AccordionItem>
    <div v-if="hasPromotions && isBorderless" class="promo-accordion__total">
      {{ $t('total_left') }}
      <span class="promo-accordion__total-amount">
        {{ (adyenTotalAmount / 100) | formattedCurrency(locale, currency.code) }}
      </span>
    </div>
  </Accordion>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { isEmpty } from '@/helpers/object'
import Accordion from '@/components/_shared/Accordion.vue'
import AccordionItem, { VARIANTS } from '@/components/_shared/AccordionItem/AccordionItem.vue'
import PromoView from '@/components/_shared/Promo/PromoView.vue'
import IconArrowDownBold from '@/components/_icons/IconArrowDownBold.svg'

export default {
  name: 'PromoAccordion',
  components: {
    Accordion,
    AccordionItem,
    PromoView,
    IconArrowDownBold,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    variant: {
      type: String,
      default: VARIANTS.small,
      validator(value) {
        return Object.values(VARIANTS).includes(value)
      },
    },
    giftCardsTotal: {
      type: Number,
      default: 0,
    },
    giftCardPayments: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      VARIANTS,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapState('page', ['locale']),
    ...mapState('order', ['adyenTotalAmount']),

    ...mapGetters({
      promotionAdjustment: 'order/promotionAdjustment',
      total: 'order/total',
    }),

    hasPromotions() {
      return !isEmpty(this.promotionAdjustment) || !!this.giftCardsTotal
    },

    isBorderless() {
      return this.variant === VARIANTS.borderless
    },
  },

  methods: {
    closeAccordion() {
      this.$refs.accordionItem.checkClose()
    },
  },
}
</script>

<style lang="scss" scoped>
.promo-accordion {
  &__icon {
    position: relative;
    width: 10px;
    height: 7px;
    margin-left: 10px;
    margin-top: 2px;

    /deep/ .accordion-item--open & {
      transform: rotate(180deg);
    }
  }

  &__reset-payments-link {
    display: inline-block;
    margin: 10px 0;
    color: $color-grey-mid-1;
    font-size: $font-size-small;
    text-decoration: underline;
    cursor: pointer;
  }

  &__total {
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid $color-grey-light-5;
    font-size: $font-size-small;
  }

  &__total-amount {
    font-weight: bold;
  }
}
</style>

<i18n src="@/locales/promo_accordion.json"></i18n>
